import React, { useEffect, useState, useRef } from "react";
import { notification, Progress, Spin } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { TrainingAPI } from "../../api/TrainingAPI";
import { playErrorSound, playSuccessSound } from "./Notifications/soundUtils";

const notificationStyle = {
  width: 500,
}

interface IParsedMessages {
  event_type: string;
  message: {
    type: string;
    content: string;
  }
  metadata: {
    persist_message: boolean,
    uuid: string
  }
  progress: number
  status: "done" | 'in-progress' | 'error' | 'success' | 'none',
  timestamp: string
}

export const RealTimeMessageNotification = ({ title = "Loading...", uuid }: { title: string, uuid: string }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [messages, setMessages] = useState<string[]>([]);
  const [api, contextHolder] = notification.useNotification();
  const eventSourceRef = useRef<EventSource | null>(null); // Track EventSource instance
  const trainingAPI = new TrainingAPI();

  useEffect(() => {
    setMessages([]);
  }, [uuid]);

  useEffect(() => {
    if (!uuid) return;

    let isMounted = true;

    async function setupEventSource() {
      try {
        const token = await getAccessTokenSilently();

        if (!isMounted) return;

        const eventSource = new EventSource(`${trainingAPI.url}/rte/${uuid}/?token=${token}`);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
          const data = event?.data;
          const parsedData = JSON.parse(data);

          setMessages(prev => [...prev, data]);

          if (parsedData?.status == "done") {
            eventSource.close();
          }
        };

        eventSource.onerror = (err) => {
          eventSource.close();
        };
      } catch (err) {
        console.error("Failed to setup EventSource:", err);
      }
    }

    setupEventSource();

    return () => {
      isMounted = false;
      if (eventSourceRef?.current) {
        eventSourceRef?.current?.close();
      }
    };
  }, [uuid, getAccessTokenSilently]);

  const messageStyling: {
    [key in IParsedMessages['status']]: {
      icon: React.ReactElement,
      fontColor: string
    }
  } = {
    'none': {
      icon: <Spin size="small" />,
      fontColor: '#000000'
    },
    'in-progress': {
      icon: <Spin size="small" />,
      fontColor: '#000000'
    },
    'done': {
      icon: <Spin size="small" />,
      fontColor: '#000000'
    },
    'success': {
      icon: <span>&#9989;</span>,
      fontColor: '#008c1c'
    },
    'error': {
      icon: <span>&#10060;</span>,
      fontColor: '#FF0000'
    }
  }

  const determineProgressStatus = (progressCompletion: number, errorPresent: boolean) => {

    if (progressCompletion < 100){ return 'active' }
    if (errorPresent) { return 'exception'} 
    else { return 'success' }
  }

  const getFormattedMessages = (messages: IParsedMessages[]) => {
    let formattedMessages: React.ReactElement[] = [];

    let progress = 0;
    const errorPresent = messages.some(e => e.status === 'error')

    messages?.forEach((msg, idx) => {
      const isLatest = idx === messages.length - 1;

      if (!(isLatest || msg?.metadata?.persist_message)) {
        return;
      }
      if (isLatest && msg?.status == "done") {
        if (errorPresent){
          playErrorSound()
        } else {
          playSuccessSound()
        }
        return;
      }

      if (msg?.event_type === "string_and_progress" || msg?.event_type === "progress_update") {
        progress = msg?.progress
      }

      if (msg?.message?.type == "text") {
        formattedMessages.push(
          <div
            key={idx}
            style={{
              color: messageStyling[msg.status].fontColor
            }}
          >
            {messageStyling[msg.status].icon} {msg?.message?.content}
          </div>
        );
      }
    })

    return (
      <>
        {progress > 0 ? <Progress
          percent={progress}
          status={determineProgressStatus(progress, errorPresent)}
          showInfo={progress < 100 ? false : true}
        /> : null}
        {formattedMessages}
      </>
    )
  }

  useEffect(() => {
    if (!uuid) return;
    if (messages?.length === 0) return;

    const parsedMessages: IParsedMessages[] = messages.map(e => JSON.parse(e))

    api.open({
      key: uuid,
      message: title,
      description: getFormattedMessages(parsedMessages),
      placement: "bottomRight",
      duration: 0,
      style: notificationStyle,
      getContainer: () => document.body,
    });
  }, [messages, uuid, title, api]);

  return <>{contextHolder}</>;
};
